import * as React from "react";
import { createRoot } from "react-dom/client";
import { useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { Point } from "ol/geom";
import { Control } from "ol/control";
import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { Map, Feature } from "ol";
import {
  Circle as CircleStyle,
  Fill,
  Stroke,
  Style,
  RegularShape,
} from "ol/style";
import { config } from "./config";
import { coordinatesConverter, EpsgCode } from "@geo/js";
import { unByKey } from "ol/Observable";
import { LocationState } from "./enums";
import { useAppContext } from "./AppContext";
import { EventsKey } from "ol/events";
import geolocationSvg from "../img/icons/geolocation-icon.svg";

const debugCoordinates = config.defaults.debugCoordinatesOnMap;
let coordinatesElement;

export interface ICoords {
  coords: IPosition;
}

export interface IPosition {
  latitude: number;
  longitude: number;
  heading?: number;
}

export const GeoLocation = ({ map }: { map: Map }): JSX.Element => {
  const { locationState, setLocationState, position, setPosition, texts } =
    useAppContext();

  const [isMapMoving, setIsMapMoving] = useState(false);

  const gpsWatcher = useRef<number>();
  const interactingWatcher = useRef<any>();
  const stationaryWatcher = useRef<EventsKey>();
  const locationStateRef = useRef<LocationState>(locationState);

  const geolocationOptions = config
    ? config.geoLocation.geolocationOptions
    : {
        maximumAge: 0,
        timeout: 15000,
        enableHighAccuracy: true,
      };

  useEffect(() => {
    locationStateRef.current = locationState;
    locationState === LocationState.Waiting
      ? startLocate()
      : locationState === LocationState.Tracking
      ? startTracking()
      : locationState === LocationState.Inactive
      ? stopLocate()
      : null;
  }, [locationState]);

  useEffect(() => {
    if (
      (position.latitude === 0 && position.longitude === 0) ||
      !centerGraphic.getGeometry()
    ) {
      return;
    }

    const location = [position.latitude, position.longitude];
    centerGraphic.getGeometry().setCoordinates(location);
    outlineGraphic.getGeometry().setCoordinates(location);
    if (headingGraphic && position) {
      const headingDeg =
        position.heading && position.heading !== null ? position.heading : 0;
      headingGraphic.getGeometry().setCoordinates(location);
      headingGraphic.set("ROTATIONRAD", (Math.PI * headingDeg) / 180);

      debugCoordinates &&
        appendCoordinate(
          position.latitude.toFixed(5) + ", " + position.longitude.toFixed(5)
        );
    }

    if (locationState === LocationState.Waiting && !isMapMoving) {
      moveToPoint(null, setLocationState(LocationState.Tracking));
    } else if (locationState === LocationState.Tracking && !isMapMoving) {
      moveToPoint(map.getView().getZoom());
    }
  }, [position]);

  const moveToPoint = (zoom?, onFitDone?) => {
    setIsMapMoving(true);
    map.getView().fit(centerGraphic.getGeometry(), {
      duration: 1000,
      maxZoom: zoom || 15,
      callback: () => {
        setIsMapMoving(false);
        onFitDone && onFitDone();
      },
    });
  };

  const onSuccess = (position: ICoords) => {
    const point = coordinatesConverter.convert(
      [position.coords.longitude, position.coords.latitude],
      EpsgCode.Wgs84,
      EpsgCode.WebMercator
    );

    setPosition({
      latitude: point[1],
      longitude: point[0],
      heading: position.coords.heading,
    });
    document.dispatchEvent(
      new CustomEvent("currentPosition", { detail: [point[1], point[0]] })
    );
  };

  const onError = () => {
    setLocationState(LocationState.Inactive);
  };

  const startLocate = () => {
    debugCoordinates && createCoordinatesNode();
    gpsWatcher.current = navigator.geolocation.watchPosition(
      onSuccess,
      onError,
      geolocationOptions
    );
    placeMarker();
  };

  const stopLocate = () => {
    debugCoordinates && removeCoordinatesNode();
    gpsWatcher.current && navigator.geolocation.clearWatch(gpsWatcher.current);

    stopTracking();
    removeMarker();
  };

  const startTracking = () => {
    //@ts-ignore
    const view = map.getView();

    if (!interactingWatcher.current) {
      interactingWatcher.current = setInterval(() => {
        if (view.getInteracting() && locationState != LocationState.Watching) {
          setLocationState(LocationState.Watching);
        }
      }, 150);
    }
    if (!stationaryWatcher.current) {
      //@ts-ignore
      stationaryWatcher.current = map.on("moveend", refreshPosition);
    }

    if (!view.getInteracting() && !view.getAnimating()) {
      refreshPosition();
    }

    document.addEventListener("stopTracking", () => {
      stopTracking();
      setLocationState(LocationState.Watching);
    });
  };

  const stopTracking = () => {
    if (stationaryWatcher.current) {
      unByKey(stationaryWatcher.current);
      stationaryWatcher.current = null;
    }
    if (interactingWatcher.current) {
      clearInterval(interactingWatcher.current);
      interactingWatcher.current = null;
    }
    document.removeEventListener("stopTracking", stopTracking);
  };

  const refreshPosition = () =>
    locationStateRef.current === LocationState.Tracking &&
    moveToPoint(map.getView().getZoom());

  const placeMarker = () => {
    const centerPoint = new Point([0, 0]);

    centerGraphic.setGeometry(centerPoint);
    outlineGraphic.setGeometry(centerPoint);
    headingGraphic.setGeometry(centerPoint);

    pointLayer.setMap(map);
    pointLayer.setSource(
      new VectorSource({
        features: [centerGraphic, outlineGraphic, headingGraphic],
      })
    );
  };

  const removeMarker = () => {
    if (!pointLayer) {
      return;
    }
    const source = pointLayer.getSource();
    source && source.clear();
    pointLayer.setMap(null);
  };

  const onClick = () =>
    setLocationState(
      locationState === LocationState.Inactive
        ? LocationState.Waiting
        : locationState === LocationState.Watching
        ? LocationState.Tracking
        : LocationState.Inactive
    );

  return (
    <>
      <div
        className={classnames("location-button", {
          "icon-locate": locationState === LocationState.Watching,
          "icon-locate-active": locationState === LocationState.Tracking,
        })}
        onClick={onClick}
        title={texts.button.location}
      >
        <img
          src={geolocationSvg}
          className={classnames({
            "icon-load rotating": locationState === LocationState.Waiting,
          })}
        />
        {/* <span
          className={classnames({
            "icon-mammadaba-locate":
              locationState === LocationState.Inactive ||
              locationState === LocationState.Watching,
            "icon-mammadaba-load rotating":
              locationState === LocationState.Waiting,
            "icon-mammadaba-locate-active":
              locationState === LocationState.Tracking,
          })}
        /> */}
      </div>
    </>
  );
};

export class LocationButton extends Control {
  constructor() {
    const element = document.createElement("div");
    element.className =
      "dabaskartes-map-button location-container ol-unselectable ol-control";
    element.id = "location-button";
    super({
      element,
    });
    const root = createRoot(element);
    root.render(<div />);
  }
}

export const createCoordinatesNode = () => {
  coordinatesElement = document.getElementById("location-coordinates");
  if (!coordinatesElement) {
    coordinatesElement = document.createElement("div");
    coordinatesElement.id = "location-coordinates";
    const domNode = document.getElementsByClassName(
      "ol-overlaycontainer-stopevent"
    )[0];
    domNode && domNode.append(coordinatesElement);
  }
};

export const removeCoordinatesNode = () => {
  coordinatesElement && coordinatesElement.remove();
};

export const appendCoordinate = (point) => {
  const pointElement = document.createElement("div");
  pointElement.textContent = point;
  coordinatesElement.append(pointElement);
};

const centerGraphicStyle = new Style({
  image: new CircleStyle({
    radius: 6,
    fill: new Fill({
      color: "#fff",
    }),
    stroke: new Stroke({
      color: "#00aeea",
      width: 4,
    }),
  }),
});

const outlineGraphicStyle = new Style({
  image: new CircleStyle({
    radius: 10,
    stroke: new Stroke({
      color: "#00aeea4d",
      width: 4,
    }),
  }),
});

const pointLayer = new VectorLayer({
  style: (f) =>
    f.getId() === "centerGraphic"
      ? centerGraphicStyle
      : f.getId() === "outlineGraphic"
      ? outlineGraphicStyle
      : f.getId() === "headingGraphic"
      ? new Style({
          image: new RegularShape({
            rotation: f.get("ROTATIONRAD"),
            radius: 8,
            points: 3,
            scale: [0.6, 1],
            displacement: [0, 18],
            fill: new Fill({
              color: "#00aeea",
            }),
            rotateWithView: true,
          }),
        })
      : undefined,
});

const centerGraphic = new Feature<Point>({ id: "centerGraphic" });
centerGraphic.setId("centerGraphic");

const outlineGraphic = new Feature<Point>({ id: "outlineGraphic" });
outlineGraphic.setId("outlineGraphic");

const headingGraphic = new Feature<Point>();
headingGraphic.setId("headingGraphic");
